import React from "react";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Link2, AlertCircle } from "lucide-react";

export interface SitelinkItem {
  label: string;
  url: string;
}

export interface SitelinksSectionProps {
  sitelinks: SitelinkItem[];
  onChange: (sitelinks: SitelinkItem[]) => void;
}

const MAX_SITELINKS = 6;
const MAX_LABEL_LENGTH = 25;

export function SitelinksSection({ sitelinks, onChange }: SitelinksSectionProps) {
  const [errors, setErrors] = useState<Record<number, string>>({});

  const validateUrl = (url: string): boolean => {
    if (!url) return true;
    return url.startsWith("http://") || url.startsWith("https://");
  };

  const handleAddSitelink = () => {
    if (sitelinks.length >= MAX_SITELINKS) return;
    onChange([...sitelinks, { label: "", url: "" }]);
  };

  const handleRemoveSitelink = (index: number) => {
    onChange(sitelinks.filter((_, i) => i !== index));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleLabelChange = (index: number, value: string) => {
    const truncated = value.slice(0, MAX_LABEL_LENGTH);
    onChange(sitelinks.map((item, i) => (i === index ? { ...item, label: truncated } : item)));
  };

  const handleUrlChange = (index: number, value: string) => {
    onChange(sitelinks.map((item, i) => (i === index ? { ...item, url: value } : item)));
    if (value && !validateUrl(value)) {
      setErrors((prev) => ({ ...prev, [index]: "URL must start with http:// or https://" }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">
              Site Links Configuration{" "}
              <span className="text-sm font-normal text-muted-foreground">
                ({sitelinks.length}/{MAX_SITELINKS})
              </span>
            </CardTitle>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddSitelink}
            disabled={sitelinks.length >= MAX_SITELINKS}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Link
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Define the site links that will appear with your ads when site link enhancements are enabled
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {sitelinks.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            No site links added. Click "Add Link" to get started.
          </div>
        ) : (
          sitelinks.map((sitelink, index) => (
            <div key={index} className="flex gap-3 items-start p-3 bg-muted/30 rounded-lg">
              <div className="flex-[35] space-y-1">
                <Label className="text-xs text-muted-foreground">
                  Label ({sitelink.label.length}/{MAX_LABEL_LENGTH})
                </Label>
                <Input
                  value={sitelink.label}
                  onChange={(e) => handleLabelChange(index, e.target.value)}
                  placeholder="e.g. Shop Now"
                  maxLength={MAX_LABEL_LENGTH}
                />
              </div>
              <div className="flex-[55] space-y-1">
                <Label className="text-xs text-muted-foreground">URL</Label>
                <Input
                  value={sitelink.url}
                  onChange={(e) => handleUrlChange(index, e.target.value)}
                  placeholder="https://example.com/page"
                  className={errors[index] ? "border-destructive" : ""}
                />
                {errors[index] && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3" />
                    {errors[index]}
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveSitelink(index)}
                className="mt-6 text-muted-foreground hover:text-destructive flex-shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
